import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

interface PageHeroProps {
  countryName?: string;
  stateName?: string;
  backLink: string;
  backLinkText: string;
  countryId?: string;
  stateId?: string;
  schoolCount?: number;
  searchLocation?: string;
}

export const PageHero = ({ 
  countryName, 
  stateName, 
  backLink, 
  backLinkText,
  countryId,
  stateId,
  schoolCount,
  searchLocation
}: PageHeroProps) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Update document title and structured data for SEO
  useEffect(() => {
    let title = "Beste Hundeschule in meiner Nähe | Hundeschulenfinder.de";
    let pageDescription = "Finde die beste Hundeschule in deiner Nähe – schnell & unkompliziert! Vergleiche, bewerte und buche direkt bei zertifizierten Hundetrainern.";
    
    // Generate dynamic title and description based on available information
    if (schoolCount !== undefined) {
      // If we have a school count, include it in the title and description
      if (searchLocation) {
        // For postal code or city searches
        title = `${schoolCount} Hundeschulen in ${searchLocation} | Beste Hundeschule in meiner Nähe`;
        pageDescription = `${schoolCount} Hundeschulen und Hundeschulkindergarten in ${searchLocation}. Vergleiche Bewertungen und finde den perfekten Hundetrainer in deiner Nähe unter ${schoolCount} geprüften Angeboten.`;
      } else if (countryName && stateName) {
        // For state pages
        title = `${schoolCount} Hundeschulen in ${stateName}, ${countryName} | Beste Hundeschule in der Nähe`;
        pageDescription = `${schoolCount} Hundeschulen und Hundeschulkindergarten in ${stateName}, ${countryName}. Vergleiche Bewertungen und finde den perfekten Hundetrainer unter ${schoolCount} geprüften Angeboten in deiner Region.`;
      } else if (countryName) {
        // For country pages
        title = `${schoolCount} Hundeschulen in ${countryName} | Beste Hundeschule in meiner Nähe`;
        pageDescription = `${schoolCount} Hundeschulen und Hundeschulkindergarten in ${countryName}. Vergleiche Bewertungen und finde den perfekten Hundetrainer unter ${schoolCount} geprüften Angeboten in deiner Region.`;
      }
    } else {
      // Fallback to original logic if no count is provided
      if (countryName && stateName) {
        title = `Beste Hundeschulen in ${stateName}, ${countryName} | Hundeschulenfinder.de`;
        pageDescription = `Die besten Hundeschulen und Hundeschulkindergarten in ${stateName}, ${countryName}. Vergleiche Bewertungen und finde den perfekten Hundetrainer in deiner Nähe.`;
      } else if (countryName) {
        title = `Beste Hundeschulen in ${countryName} | Hundeschulenfinder.de`;
        pageDescription = `Die besten Hundeschulen und Hundeschulkindergarten in ${countryName}. Vergleiche Bewertungen und finde den perfekten Hundetrainer in deiner Nähe.`;
      }
    }
    
    document.title = title;
    
    // Update meta description for better SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }
    
    // Add structured data for this specific page
    let locationName = countryName;
    if (stateName) {
      locationName = `${stateName}, ${countryName}`;
    } else if (searchLocation) {
      locationName = searchLocation;
    }
    
    if (locationName) {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `https://hundeschulenfinder.de/${countryId ? countryId : ''}${stateId ? '/' + stateId : ''}#service`,
        "name": `${schoolCount !== undefined ? `${schoolCount} ` : ''}Hundeschulen in ${locationName}`,
        "serviceType": ["Hundetraining", "Hundeschulkindergarten", "Welpenerziehung", "Hundeschule"],
        "provider": {
          "@type": "Organization",
          "@id": "https://hundeschulenfinder.de/#organization",
          "name": "Hundeschulenfinder",
          "url": "https://hundeschulenfinder.de",
          "logo": {
            "@type": "ImageObject",
            "url": "https://hundeschulenfinder.de/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png",
            "width": "180",
            "height": "60",
            "contentUrl": "https://hundeschulenfinder.de/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png"
          }
        },
        "areaServed": {
          "@type": "Place",
          "name": locationName,
          "address": {
            "@type": "PostalAddress",
            "addressCountry": countryName || "Germany",
            "addressRegion": stateName || ""
          }
        },
        "description": pageDescription,
        "image": {
          "@type": "ImageObject",
          "url": "https://hundeschulenfinder.de/og-image.png",
          "width": "1200",
          "height": "630"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5.0",
          "bestRating": "5",
          "worstRating": "1",
          "ratingCount": schoolCount?.toString() || "215",
          "reviewCount": schoolCount?.toString() || "215"
        },
        "audience": {
          "@type": "PeopleAudience",
          "audienceType": "Hundebesitzer"
        },
        "brand": {
          "@type": "Brand",
          "name": "Hundeschulenfinder",
          "logo": "https://hundeschulenfinder.de/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png"
        },
        "offers": {
          "@type": "Offer",
          "category": ["Hundetraining", "Hundeschulkindergarten", "Welpenerziehung"],
          "availability": "https://schema.org/InStock"
        },
        "keywords": ["beste Hundeschule in meiner Nähe", "Hundeschulkindergarten", "Hundetraining", "Welpenerziehung"]
      };
      
      // Add FAQ section
      const faqData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `https://hundeschulenfinder.de/${countryId ? countryId : ''}${stateId ? '/' + stateId : ''}#faq`,
        "mainEntity": [
          {
            "@type": "Question",
            "name": `Wie finde ich die beste Hundeschule in ${locationName}?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `Mit Hundeschulenfinder.de kannst du schnell und einfach die beste Hundeschule in ${locationName} finden. Gib einfach deine Postleitzahl ein, vergleiche Bewertungen und wähle einen qualifizierten Hundetrainer aus.`
            }
          },
          {
            "@type": "Question",
            "name": `Was bieten Hundeschulen in ${locationName} an?`,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": `Hundeschulen in ${locationName} bieten verschiedene Kurse wie Welpentraining, Hundeschulkindergarten, Grundgehorsam, Agility und Einzeltraining an. Die meisten Hundeschulen passen ihre Angebote individuell an die Bedürfnisse von Hund und Halter an.`
            }
          }
        ]
      };
      
      // Remove any existing structured data elements for this component
      const existingScripts = document.querySelectorAll('script[data-page-schema]');
      existingScripts.forEach(script => script.remove());
      
      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      script.setAttribute('data-page-schema', 'true');
      document.head.appendChild(script);
      
      // Add FAQ data as separate script
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify(faqData);
      faqScript.setAttribute('data-page-faq', 'true');
      document.head.appendChild(faqScript);
      
      // Update canonical URL to avoid indexing issues with redirects
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        const canonicalUrl = `https://hundeschulenfinder.de/${countryId ? countryId : ''}${stateId ? '/' + stateId : ''}`;
        canonicalLink.setAttribute('href', canonicalUrl);
      }
      
      // Clean up when component unmounts
      return () => {
        const scriptsToRemove = document.querySelectorAll('script[data-page-schema], script[data-page-faq]');
        scriptsToRemove.forEach(script => script.remove());
      };
    }
  }, [countryName, stateName, countryId, stateId, schoolCount, searchLocation]);

  return (
    <>
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        onClick={() => navigate(backLink)}
        className="flex items-center text-gray-600 hover:text-primary mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {backLinkText}
      </motion.button>
      
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        {countryName && stateName ? (
          <div className="flex flex-col items-center space-y-2">
            {countryId ? (
              <Link to={`/${countryId}`} className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full inline-block hover:bg-primary/20 transition-colors">
                {countryName}
              </Link>
            ) : (
              <span className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full inline-block">
                {countryName}
              </span>
            )}
            
            {stateId && countryId ? (
              <Link to={`/${countryId}/${stateId}`} className="px-3 py-1 text-xs font-medium bg-primary/5 text-primary rounded-full inline-block hover:bg-primary/15 transition-colors">
                {stateName}
              </Link>
            ) : (
              <span className="px-3 py-1 text-xs font-medium bg-primary/5 text-primary rounded-full inline-block">
                {stateName}
              </span>
            )}
          </div>
        ) : countryName ? (
          countryId ? (
            <Link to={`/${countryId}`} className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full inline-block mb-4 hover:bg-primary/20 transition-colors">
              {countryName}
            </Link>
          ) : (
            <span className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full inline-block mb-4">
              {countryName}
            </span>
          )
        ) : searchLocation ? (
          <span className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full inline-block mb-4">
            {searchLocation}
          </span>
        ) : null}
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold my-4 text-gray-900">
          {schoolCount !== undefined ? (
            <>
              {schoolCount} {t('mainHeadingWithCount')}
            </>
          ) : (
            t('mainHeading')
          )}
        </h1>
        
        <div className="flex justify-center mt-3 mb-6">
          <img 
            src="/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png" 
            alt="Hundeschulenfinder Logo - Beste Hundeschulen & Hundeschulkindergarten in Deutschland, Österreich und der Schweiz" 
            className="h-16 md:h-20"
          />
        </div>
        
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          {schoolCount !== undefined ? (
            t('subHeadingWithCount')
          ) : (
            t('subHeading')
          )}
        </p>
      </motion.section>
    </>
  );
};
